import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FaSearch, FaSpinner, FaPlus } from 'react-icons/fa';
import AddVacanteModal from './AddVacanteModal';
import { useAuth } from '../context/AuthContext';
import axiosInstance from '../utils/axiosConfig';
import { VACANTES_ADMIN_ROLES, diasDesde, rutaPuesto, coincideBusqueda } from './vacantesShared';
import './VacantesUI.css';

/**
 * Lista de vacantes vigentes para elegir el puesto que alguien va a ocupar
 * (ingreso o movimiento interno). Si el puesto no esta, GH lo abre aqui mismo
 * y queda elegido.
 *
 * `onAbriendoVacante(bool)` avisa cuando el modal de abrir vacante esta
 * encima, para que el modal de afuera no se cierre con Escape.
 */
function VacantePicker({ intro, onElegir, excluirIds = [], onAbriendoVacante, onError }) {
  const { user } = useAuth();
  const puedeAbrirVacante = VACANTES_ADMIN_ROLES.includes(user?.role);

  const [vacantes, setVacantes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [abriendo, setAbriendo] = useState(false);

  const cargar = useCallback(async () => {
    try {
      setCargando(true);
      const response = await axiosInstance.get('/api/vacantes?estado=vigentes');
      setVacantes(response.data);
    } catch (err) {
      console.error('Error al cargar vacantes:', err);
      onError?.('No se pudieron cargar las vacantes');
    } finally {
      setCargando(false);
    }
  }, [onError]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const setAbriendoVacante = (valor) => {
    setAbriendo(valor);
    onAbriendoVacante?.(valor);
  };

  const filtradas = useMemo(
    () => vacantes
      .filter((v) => !excluirIds.includes(v.id) && coincideBusqueda(v, busqueda))
      .sort((a, b) => (a.titulo || '').localeCompare(b.titulo || '', 'es', { sensitivity: 'base' })),
    [vacantes, busqueda, excluirIds]
  );

  return (
    <>
      {intro && <p className="text-sm text-gray-600 mb-4">{intro}</p>}
      <div className="vac-buscar">
        <FaSearch />
        <input
          type="search"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder="Buscar por cargo, área, gerencia o supervisor"
          aria-label="Buscar vacante"
          autoFocus
        />
      </div>

      {cargando ? (
        <div className="vac-vacio"><p><FaSpinner className="animate-spin inline mr-2" />Cargando vacantes...</p></div>
      ) : vacantes.length === 0 ? (
        <div className="vac-vacio">
          <p>No hay vacantes abiertas.</p>
          <p>
            {puedeAbrirVacante
              ? 'Abre la vacante del puesto para continuar.'
              : 'Pídele a Gestión Humana que abra la vacante del puesto.'}
          </p>
        </div>
      ) : filtradas.length === 0 ? (
        <div className="vac-vacio">
          <p>{busqueda ? `Ninguna vacante coincide con "${busqueda}".` : 'No hay otras vacantes abiertas.'}</p>
        </div>
      ) : (
        <ul className="vac-lista">
          {filtradas.map((v) => {
            const dias = diasDesde(v.fecha_creacion);
            return (
              <li key={v.id}>
                <button type="button" className="vac-opcion" onClick={() => onElegir(v)}>
                  <span className="vac-asiento" />
                  <span className="vac-opcion-cuerpo">
                    <span className="vac-puesto-titulo block">{v.titulo}</span>
                    <span className="vac-puesto-ruta block">{rutaPuesto(v)}</span>
                  </span>
                  <span className="vac-opcion-meta">
                    <span>{v.supervisor_nombre ? `Reporta a ${v.supervisor_nombre}` : 'Sin supervisor'}</span>
                    <span>{v.motivo}, {dias === 1 ? '1 día' : `${dias} días`}</span>
                    {v.candidatos_activos_count > 0 && (
                      <span className="font-medium text-blue-700">
                        {v.candidatos_activos_count === 1 ? '1 candidato' : `${v.candidatos_activos_count} candidatos`}
                      </span>
                    )}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <div className="vac-pie-selector">
        <span>¿El puesto no está en la lista?</span>
        {puedeAbrirVacante ? (
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setAbriendoVacante(true)}>
            <FaPlus /> Abrir vacante
          </button>
        ) : (
          <span className="text-gray-500">Gestión Humana tiene que abrir la vacante primero.</span>
        )}
      </div>

      {abriendo && (
        <AddVacanteModal
          onClose={() => setAbriendoVacante(false)}
          onSuccess={(nueva) => {
            setAbriendoVacante(false);
            cargar();
            if (nueva) onElegir(nueva);
          }}
        />
      )}
    </>
  );
}

export default VacantePicker;
